import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowRight, BadgeCheck, ListMusic, SlidersHorizontal, ChevronDown } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=1920&q=80&auto=format&fit=crop";
const HERO_CARD =
  "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=1200&q=80&auto=format&fit=crop";

const LINES = ["Votre soirée.", "Votre musique.", "Notre métier."];

const BADGES = [
  { icon: BadgeCheck, label: "Tarifs transparents" },
  { icon: ListMusic, label: "Playlist collaborative" },
  { icon: SlidersHorizontal, label: "Matériel professionnel" },
];

export default function Hero() {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} className="relative flex min-h-screen items-center overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src={HERO_BG} alt="" className="h-[120%] w-full object-cover opacity-35" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#080C14]/70 via-[#080C14]/55 to-[#080C14]" />
      <div
        className="animate-pulse-glow absolute -top-32 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full"
        style={{ background: "radial-gradient(closest-side, rgba(0,140,255,0.22), transparent)" }}
      />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-24 pt-36 sm:px-6 lg:grid-cols-12 lg:px-8 lg:pt-28">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-mono-brand text-xs uppercase tracking-[0.3em] text-cyan-400"
            data-testid="hero-eyebrow"
          >
            DJ · Animation musicale — Mariages & événements
          </motion.p>

          <h1 className="font-display mt-6 text-5xl font-black leading-[1.02] tracking-tight text-slate-50 sm:text-6xl lg:text-7xl" data-testid="hero-title">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "text-gradient" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.25 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subtitle"
          >
            KLMProd imagine des ambiances musicales sur mesure pour vos mariages, soirées privées et
            séminaires. Préparation rigoureuse, communication claire et une piste de danse pleine
            jusqu'au bout de la nuit.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-devis"
              onClick={() => lenis?.scrollTo("#devis", { offset: -72, duration: 1.5 })}
              className="bg-brand-gradient group flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-[#04121f] shadow-[0_0_40px_rgba(0,150,255,0.35)] transition-transform duration-300 hover:scale-105 active:scale-95"
            >
              Demander un devis
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-formules"
              onClick={() => lenis?.scrollTo("#formules", { offset: -72, duration: 1.5 })}
              className="rounded-full border border-cyan-400/30 px-7 py-3.5 text-sm font-semibold text-cyan-300 transition-colors duration-300 hover:border-cyan-400/60 hover:bg-cyan-400/10"
            >
              Découvrir les formules
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.25 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
            data-testid="hero-badges"
          >
            {BADGES.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-sm text-slate-400">
                <Icon size={16} className="text-cyan-400" />
                {label}
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          className="hidden lg:col-span-5 lg:block"
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          style={{ y: cardY }}
        >
          <motion.div
            data-testid="hero-visual-card"
            onMouseMove={onMove}
            onMouseLeave={() => {
              mx.set(0);
              my.set(0);
            }}
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d", perspective: 900 }}
            className="relative rounded-3xl border border-cyan-400/25 p-2 shadow-[0_30px_80px_rgba(0,80,200,0.35)]"
          >
            <div className="overflow-hidden rounded-2xl">
              <img src={HERO_CARD} alt="DJ KLMProd en mix" className="aspect-[4/5] w-full object-cover" />
            </div>
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-[#080C14]/60 via-transparent to-transparent" />
            <div
              className="absolute bottom-6 left-6 right-6 rounded-2xl border border-cyan-400/20 bg-[#0E1524]/80 p-4 backdrop-blur-xl"
              style={{ transform: "translateZ(40px)" }}
            >
              <p className="font-mono-brand text-[10px] uppercase tracking-[0.25em] text-cyan-400">Le jour J</p>
              <p className="mt-1 text-sm font-semibold text-slate-100">
                Une programmation verrouillée 48 h avant, pour une soirée sans fausse note.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => lenis?.scrollTo("#methode", { offset: -72, duration: 1.4 })}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-cyan-300/70 transition-colors hover:text-cyan-300"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        aria-label="Défiler vers le bas"
      >
        <ChevronDown size={26} />
      </motion.button>
    </section>
  );
}
